import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import { PAGE, LIMIT } from "src/constants/searchParams";
import { useSearchParams } from "react-router-dom";
import { OrderWithPrescription } from "src/api/types";
import { Box, Checkbox, TableCell } from "@mui/material";
import { ChangeEvent, useCallback, useMemo } from "react";
import { columns } from "../../utils/columns";
import { ROWS_PER_PAGE } from "../../utils/rowPerPage";
import OrdersTableRow from "../OrdersTableRow/OrdersTableRow";
import { useSelection } from "src/context/ItemsSelectionProvider/useSelection";
import { useFilters } from "src/hooks/useFilters";
import TableHeaderCell from "src/shared/TableHeaderCell";

interface Props {
  orders: OrderWithPrescription[];
  total: number;
}

function OrdersTable({ orders, total }: Props) {
  const [limit] = useFilters(LIMIT, ROWS_PER_PAGE[0].toString());
  const [page] = useFilters(PAGE, "0");
  const [params, setParams] = useSearchParams();
  const { setSelected, selected } = useSelection();

  //104 header + filters
  //64 gaps
  //32 top and bottom padding
  //4 px loading
  const height = useMemo(() => window.innerHeight - 104 - 64 - 32 - 4, []);

  const handleChangePage = (_: unknown, newPage: number) => {
    params.set(PAGE, newPage.toString());
    setParams(params);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    params.set(LIMIT, event.target.value);
    params.set(PAGE, "0");
    setParams(params);
  };

  const handleSelectAll = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (e.target.checked) {
        setSelected(orders.map((order) => order.id));
      } else {
        setSelected([]);
      }
    },
    [orders, setSelected],
  );

  const isChecked = useMemo(
    () => orders.length > 0 && orders.length === selected.length,
    [orders.length, selected.length],
  );

  const isIndeterminate = useMemo(
    () => selected.length > 0 && selected.length < orders.length,
    [orders.length, selected.length],
  );

  const currentPage = useMemo(() => {
    return total == null || total <= 0 ? 0 : Number(page);
  }, [page, total]);

  return (
    <Box>
      <TableContainer sx={{ height: height - 52 }}>
        <Table stickyHeader aria-label="sticky table">
          <TableHead>
            <TableRow>
              <TableCell padding="none">
                <Checkbox
                  color="primary"
                  aria-label="select all orders"
                  onChange={handleSelectAll}
                  checked={isChecked}
                  indeterminate={isIndeterminate}
                />
              </TableCell>
              {columns.map((column) => (
                <TableHeaderCell key={column.id} column={column} />
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {orders.map((row) => (
              <OrdersTableRow row={row} key={row.id} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        showFirstButton
        showLastButton
        sx={{ height: 52, mr: 10 }}
        rowsPerPageOptions={ROWS_PER_PAGE}
        component="div"
        count={total}
        rowsPerPage={Number(limit)}
        page={currentPage}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Box>
  );
}

export default OrdersTable;
